import type { ReactElement, ReactNode } from 'react';
import styled from 'styled-components';

export type TooltipPlacement = 'bottom-left' | 'bottom-right' | 'top-left' | 'top-right';

interface Props {
  /** Text/markup shown inside the floating bubble. */
  content: ReactNode;
  /** Which edge the bubble hangs from (default `bottom-right`). */
  placement?: TooltipPlacement;
  /** When set (px), the bubble wraps to this width instead of a single nowrap line. */
  maxWidth?: number;
  className?: string;
  children: ReactNode;
}

const Wrapper = styled.span<{ $placement: TooltipPlacement; $maxWidth?: number }>`
  position: relative;
  display: inline-flex;

  .cc-tooltip {
    position: absolute;
    ${({ $placement }): string =>
      $placement.startsWith('top')
        ? 'bottom: calc(100% + 8px); top: auto;'
        : 'top: calc(100% + 8px); bottom: auto;'}
    ${({ $placement }): string =>
      $placement.endsWith('left') ? 'left: 0; right: auto;' : 'right: 0; left: auto;'}
    background: #0a1326;
    border: 1px solid rgba(255, 255, 255, 0.14);
    color: #d5ddee;
    font-weight: 600;
    font-size: 11px;
    padding: 6px 9px;
    border-radius: 7px;
    ${({ $maxWidth }): string =>
      $maxWidth === undefined
        ? 'white-space: nowrap;'
        : `white-space: normal; max-width: ${$maxWidth}px; line-height: 1.5;`}
    opacity: 0;
    pointer-events: none;
    transition: opacity 0.14s;
    z-index: 30;
    box-shadow: 0 6px 18px rgba(0, 0, 0, 0.4);
  }

  &:hover .cc-tooltip,
  &:focus-within .cc-tooltip {
    opacity: 1;
  }
`;

/*
 * Lightweight hover tooltip. Wraps any trigger and reveals a floating bubble on
 * hover/focus — no portals, positioned relative to the trigger. The trigger may
 * be disabled (pointer-events: none) since hover is tracked on the wrapper.
 */
export const Tooltip = ({
  content,
  placement = 'bottom-right',
  maxWidth,
  className,
  children
}: Props): ReactElement => (
  <Wrapper $placement={placement} $maxWidth={maxWidth} className={className}>
    {children}
    <span className='cc-tooltip'>{content}</span>
  </Wrapper>
);
